// src/stores/permissionsStore.js
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { db } from '@/services/firebaseconfig'
import { doc, setDoc } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { logEvent } from '@/utils/firebase/eventLogger'
import { diffAccessMatrix, isNoOpDiff } from '@/utils/accessMatrixDiff'

export const usePermissionsStore = defineStore('permissionsStore', () => {
  const permissions = ref({
    pages: {}, // { 'Dashboard': ['admin', 'sales'] }
    collections: {}, // { 'customers': { read: ['admin'], write: ['admin'] } }
  })
  const isLoading = ref(false)
  const isInitialized = ref(false)

  let _unsub = null
  let _initPromise = null
  let _initResolve = null

  // Subscribe to real-time updates so the UI reflects changes immediately.
  // With Firestore persistence enabled, onSnapshot fires cache-first (stale data)
  // then again with server data. We resolve ONLY on server-confirmed data to prevent
  // route guards from checking stale permissions and blocking valid users.
  function init() {
    if (_unsub) return _initPromise

    _initPromise = new Promise(resolve => {
      _initResolve = resolve
    })

    // Unchanged server data produces a metadata-only snapshot after the cache.
    _unsub = onSnapshot(
      doc(db, 'system', 'accessControl'),
      { includeMetadataChanges: true },
      snap => {
        if (snap.exists()) {
          permissions.value = snap.data()
        } else {
          permissions.value = { pages: {}, collections: {} }
        }

        // Only resolve initialization on server-confirmed data.
        // Cache snapshots (fromCache=true) may have stale permissions.
        const fromCache = snap.metadata?.fromCache ?? false
        if (!fromCache || isInitialized.value) {
          isInitialized.value = true
          if (_initResolve) {
            _initResolve()
            _initResolve = null
          }
        }
      }
    )

    // Safety timeout: if we're offline or server snapshot never arrives,
    // resolve with cached data after 3 seconds rather than hanging forever.
    setTimeout(() => {
      if (_initResolve) {
        console.warn('[permissionsStore] Server snapshot timeout — resolving with cached data')
        isInitialized.value = true
        _initResolve()
        _initResolve = null
      }
    }, 3000)

    return _initPromise
  }

  // Wait for permissions to be loaded (for use in navigation guards)
  async function waitForInit() {
    if (isInitialized.value) return
    if (!_initPromise) init()
    return _initPromise
  }

  function stop() {
    if (_unsub) {
      _unsub()
      _unsub = null
    }
    isInitialized.value = false
    _initPromise = null
    _initResolve = null
    permissions.value = { pages: {}, collections: {} }
  }

  // The audit log lives HERE rather than in AccessControlPage because this is the
  // only writer of system/accessControl from the client, and three separate
  // handlers call it (saveChanges, removeStalePage, removeAllStalePages). Logging
  // at the call sites would have been three chances to forget — and the screen
  // shipped with zero of them (CODING_RULES §2.1), which is why nobody could tell
  // who widened collections.unknownInbound on 2026-08-20.
  //
  // `source` identifies which handler wrote, since the three mean different things.
  async function savePermissions(newPermissions, { source = 'unknown' } = {}) {
    // Snapshot BEFORE the write. Firestore applies the write locally the instant
    // setDoc is called, so onSnapshot repoints permissions.value at the NEW matrix
    // before the promise resolves — read it afterwards and the diff is empty.
    const beforeMatrix = JSON.parse(JSON.stringify(permissions.value || {}))

    try {
      await setDoc(doc(db, 'system', 'accessControl'), newPermissions, { merge: true })
    } catch (e) {
      console.error('Failed to save permissions:', e)
      throw e
    }

    // Only after the write is confirmed — an event for a save that threw would be
    // a record of something that never happened. A logging failure must never
    // fail the save: logEvent swallows its own errors, and this catch covers the
    // diff itself so a malformed matrix cannot turn a successful save into a
    // thrown error in the caller's UI.
    try {
      const diff = diffAccessMatrix(beforeMatrix, newPermissions)
      if (!isNoOpDiff(diff)) {
        await logEvent({
          eventType: 'ACCESS_CONTROL_UPDATED',
          action: 'update',
          resource: { type: 'system', id: 'accessControl' },
          changes: { fields: diff.fields, before: diff.before, after: diff.after },
          context: {
            source,
            metadata: {
              ...diff.summary,
              delta: diff.delta,
              truncated: diff.truncated,
              // Keys the caller dropped that the merge write did NOT delete.
              // Recorded because the UI reports these as removed and Firestore
              // keeps them — see src/utils/accessMatrixDiff.js.
              notRemovedByMerge: diff.notRemovedByMerge,
            },
          },
        })
      }
    } catch (e) {
      console.warn('[permissionsStore] Access-control audit log failed:', e?.message)
    }

    return true
  }

  /**
   * Check if a user with specific roles can access a page.
   *
   * Rules:
   * - No resourceKey (pageKey is falsy) → public, allow
   * - SuperAdmin (via auth claim) → always allow
   * - Page has entry in matrix with roles listed → user must have ≥1 matching role
   * - Page has entry in matrix but empty array → locked (superAdmin only)
   * - Page has NO entry in matrix (undefined) → DENY by default
   */
  function canAccessPage(pageKey, userRoles = [], isSuperAdmin = false) {
    if (!pageKey) return true

    // SuperAdmin bypasses all page restrictions (auth claim, not a role string)
    if (isSuperAdmin) return true

    const normalizedUserRoles = userRoles.map(r => r.toLowerCase())

    const pageEntry = permissions.value.pages?.[pageKey]

    // Not in the matrix at all → deny by default
    if (pageEntry === undefined) return false

    // In the matrix but empty array → locked to superAdmin only
    if (Array.isArray(pageEntry) && pageEntry.length === 0) return false

    // Has allowed roles → check intersection
    const allowed = Array.isArray(pageEntry) ? pageEntry : []
    return allowed.some(role => normalizedUserRoles.includes(role.toLowerCase()))
  }

  function canReadCollection(collectionKey, userRoles = [], isSuperAdmin = false) {
    if (isSuperAdmin) return true
    if (!permissions.value.collections?.[collectionKey]) return false
    const allowed = (permissions.value.collections[collectionKey].read || []).map(r =>
      r.toLowerCase()
    )
    return userRoles.map(r => r.toLowerCase()).some(role => allowed.includes(role))
  }

  function canWriteCollection(collectionKey, userRoles = [], isSuperAdmin = false) {
    if (isSuperAdmin) return true
    if (!permissions.value.collections?.[collectionKey]) return false
    const allowed = (permissions.value.collections[collectionKey].write || []).map(r =>
      r.toLowerCase()
    )
    return userRoles.map(r => r.toLowerCase()).some(role => allowed.includes(role))
  }

  /**
   * Check if a user's roles grant full (unscoped) visibility on a collection.
   *
   * This replaces ALL hardcoded privileged-role arrays throughout the codebase.
   * Roles listed in `fullVisibility` see every record in the collection.
   * Roles NOT listed fall through to org-owner → ownership → fallback scoping.
   *
   * Rules:
   * - SuperAdmin → always true
   * - Role listed in collections[key].fullVisibility → true
   * - Missing collection or empty array → false (deny by default)
   */
  function hasFullVisibility(collectionKey, userRoles = [], isSuperAdmin = false) {
    if (isSuperAdmin) return true
    const entry = permissions.value.collections?.[collectionKey]
    if (!entry?.fullVisibility?.length) return false
    const allowed = entry.fullVisibility.map(r => r.toLowerCase())
    return userRoles.map(r => r.toLowerCase()).some(role => allowed.includes(role))
  }

  return {
    permissions,
    isLoading,
    isInitialized,
    init,
    waitForInit,
    stop,
    savePermissions,
    canAccessPage,
    canReadCollection,
    canWriteCollection,
    hasFullVisibility,
  }
})
